import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import api from "./api/axiosConfig";
import logo from "../src/assets/LogoIcon.png";
import bgImage from "../src/assets/onboardingBg.png";
import { Eye, EyeOff } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

// Yup validation schema
const validationSchema = Yup.object({
  email: Yup.string()
    .required("Email address is required")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      "Please enter a valid email address (e.g. user@example.com)"
    ),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(/^\S+$/, "Password must not contain spaces"),
});

export default function Login({ onLogin }) {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      try {
        const res = await api.post("/user/login", {
          email: values.email,
          password: values.password,
        });

        if (res.data.status) {
          if (res.data.data.user.role === "ADMIN") {
            localStorage.setItem("token", res.data.data.accessToken);
            localStorage.setItem("refreshToken", res.data.data.refreshToken);
            localStorage.setItem("role", res.data.data.user.role);
            localStorage.setItem("email", res.data.data.user.email);

            toast.success(res.data.message, {
              duration: 3000,
              position: "top-center",
              style: {
                background: "#10b981",
                color: "#fff",
                fontWeight: "600",
                padding: "16px",
                fontSize: "14px",
              },
              iconTheme: {
                primary: "#fff",
                secondary: "#10b981",
              },
            });

            onLogin(res.data.data.user.role);

            setTimeout(() => {
              navigate("/dashboard");
            }, 300);
          } else {
            const errorMsg = "Access denied. Only admin users can login.";
            setFieldError("email", errorMsg);
            toast.error(errorMsg, {
              duration: 4000,
              position: "top-center",
              style: {
                background: "#ef4444",
                color: "#fff",
                fontWeight: "600",
                padding: "16px",
                fontSize: "14px",
              },
            });
          }
        } else {
          const errorMsg = res.data.message || "Login failed. Please try again.";
          setFieldError("email", errorMsg);
          toast.error(errorMsg, {
            duration: 4000,
            position: "top-center",
            style: {
              background: "#ef4444",
              color: "#fff",
              fontWeight: "600",
              padding: "16px",
              fontSize: "14px",
            },
          });
        }
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          "Invalid email or password. Please try again.";
        setFieldError("email", errorMessage);
        toast.error(errorMessage, {
          duration: 4000,
          position: "top-center",
          style: {
            background: "#ef4444",
            color: "#fff",
            fontWeight: "600",
            padding: "16px",
            fontSize: "14px",
          },
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <div
      className="min-h-screen flex flex-col bg-cover bg-center"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      {/* Toast Container */}
      <Toaster />

      {/* HEADER */}
      <div className="w-full bg-white/80 backdrop-blur py-2 md:py-4 px-4 md:px-8 flex items-center shadow-sm">
        <img src={logo} alt="Takeout25" className="h-12 md:h-16 object-contain" />
      </div>

      {/* LOGIN AREA */}
      <div className="flex flex-1 justify-center items-center px-4 py-4 md:py-8 lg:py-12">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-5 sm:p-6 md:p-8 flex flex-col">
          {/* TITLE */}
          <div className="text-center mb-4 md:mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-green-700">
              20fy.ai Admin
            </h1>
            <p className="text-gray-500 text-xs md:text-sm mt-1">
              Sign in to manage customer subscriptions
            </p>
          </div>

          {/* FORM */}
          <form onSubmit={formik.handleSubmit} className="flex flex-col flex-1">
            <div className="space-y-4 md:space-y-6">
              {/* EMAIL */}
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                  Email address
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="admin@test.com"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`w-full px-3 md:px-4 py-2 text-sm md:text-base border rounded-lg focus:ring-2 focus:outline-none ${
                    formik.touched.email && formik.errors.email
                      ? "border-red-400 focus:ring-red-300 bg-red-50"
                      : "border-gray-300 focus:ring-green-500"
                  }`}
                />
                {formik.touched.email && formik.errors.email && (
                  <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                    ⚠️ {formik.errors.email}
                  </p>
                )}
              </div>

              {/* PASSWORD */}
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="••••••••"
                    value={formik.values.password}
                    onChange={(e) => {
                      const noSpaces = e.target.value.replace(/\s/g, "");
                      formik.setFieldValue("password", noSpaces);
                    }}
                    onBlur={formik.handleBlur}
                    className={`w-full px-3 md:px-4 py-2 text-sm md:text-base border rounded-lg focus:ring-2 focus:outline-none pr-10 ${
                      formik.touched.password && formik.errors.password
                        ? "border-red-400 focus:ring-red-300 bg-red-50"
                        : "border-gray-300 focus:ring-green-500"
                    }`}
                  />
                  {/* EYE ICON */}
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {formik.touched.password && formik.errors.password && (
                  <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                    ⚠️ {formik.errors.password}
                  </p>
                )}
              </div>
            </div>

            {/* BUTTON */}
            <div className="mt-auto pt-4 md:pt-6">
              <button
                type="submit"
                disabled={formik.isSubmitting}
                className={`w-full py-2 md:py-2.5 text-sm md:text-base rounded-lg font-semibold transition flex items-center justify-center gap-2 ${
                  formik.isSubmitting
                    ? "bg-green-700 cursor-not-allowed text-white"
                    : "bg-green-900 hover:bg-green-700 text-white"
                }`}
              >
                {formik.isSubmitting && (
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                )}
                {formik.isSubmitting ? "Signing In..." : "Sign In"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* FOOTER */}
      <footer className="text-center text-xs text-gray-500 py-2 md:pb-4">
        © 2026 20fy.ai
      </footer>
    </div>
  );
}